/* eslint-disable i18next/no-literal-string */
import {Text} from '@dropbox/dig-components/typography';
import {Stack} from '@dropbox/dig-foundations';
import type {AskHealthVersion} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {MiniChart} from 'components/DSYS/MiniChart';

import styles from './AskHealth.module.css';
import {percent, seconds, shortDate} from './format';
import {Panel} from './Panel';
import {StatTile} from './StatTile';
import {extremeBefore, offlineQuality} from './versions';
import {VersionsTable} from './VersionsTable';

type Series = {name: string; value: (v: AskHealthVersion) => number | null | undefined};

const VersionChart = ({
  title,
  description,
  versions,
  series,
}: {
  title: string;
  description: string;
  versions: AskHealthVersion[];
  series: Series[];
}) => (
  <Panel>
    <Stack gap="8">
      <Text tagName="div" isBold>
        {title}
      </Text>
      <Text tagName="div" size="small" color="subtle">
        {description}
      </Text>
      <MiniChart
        height={180}
        graph={{
          kind: 'line',
          labelHeader: 'Version',
          labels: versions.map((v) => shortDate(v.date)),
          series: series.map((s) => ({
            name: s.name,
            values: versions.map((v) => Math.round((s.value(v) ?? 0) * 10) / 10),
          })),
        }}
      />
    </Stack>
  </Panel>
);

const wasOn = (v: AskHealthVersion | undefined, value: string) =>
  v ? `was ${value} on ${shortDate(v.date)}` : 'no earlier version';

/**
 * Every Ask version since June on the same offline questions, the way the
 * quality-history report showed it: headline change, quality and time by
 * version, and what changed in each.
 */
export const VersionsHistory = ({versions}: {versions: AskHealthVersion[]}) => {
  const replay = versions.filter((v) => !v.prod);
  const prod = versions.filter((v) => v.prod).at(-1);
  const today = replay.at(-1);
  const before = replay.at(-2);
  if (!today || !before) {
    return null;
  }
  const lowestQuality = extremeBefore(replay, offlineQuality, 'min');
  const slowest = extremeBefore(replay, (v) => v.p90_seconds, 'max');
  const mostDash = extremeBefore(replay, (v) => v.dash_agent, 'max');
  const todayQuality = offlineQuality(today);
  return (
    <Stack gap="12">
      <Stack gap="2">
        <Text tagName="div" size="large" isBold>
          Ask versions since {shortDate(replay[0].date)}
        </Text>
        <Text tagName="div" size="small" color="subtle">
          Every version of Ask rerun on the same 49 real employee questions, offline. This is how
          the code changed; the numbers above are live traffic.
        </Text>
      </Stack>

      <div className={styles.historyTiles}>
        <StatTile
          label="Answer quality"
          value={todayQuality == null ? '—' : `${Math.round(todayQuality)} / 100`}
          note={wasOn(
            lowestQuality,
            `${Math.round(lowestQuality ? (offlineQuality(lowestQuality) ?? 0) : 0)}`
          )}
        />
        <StatTile
          label={`Better than ${shortDate(before.date)}'s Ask`}
          value={percent(before.today_preferred)}
          note="of questions, side by side"
        />
        <StatTile
          label="Slowest 10% of answers"
          value={seconds(today.p90_seconds)}
          note={wasOn(slowest, seconds(slowest?.p90_seconds))}
        />
        <StatTile
          label="Sent to the Dash agent"
          value={percent(today.dash_agent)}
          note={wasOn(mostDash, percent(mostDash?.dash_agent))}
        />
      </div>

      <div className={styles.charts}>
        <VersionChart
          title="Answer quality by version"
          description="Out of 100. Offline judges are Opus and gpt-6.1-sol on a rubric; the live judge is the one scoring prod traffic."
          versions={replay}
          series={[
            {name: 'Offline judges', value: offlineQuality},
            {name: 'Live judge', value: (v) => (v.quality == null ? null : 100 * v.quality)},
          ]}
        />
        <VersionChart
          title="Answer time by version"
          description="Seconds to the full answer. The slowest are mostly ones handed to the Dash agent."
          versions={replay}
          series={[
            {name: 'Median (s)', value: (v) => v.median_seconds},
            {name: 'Slowest 10% (s)', value: (v) => v.p90_seconds},
          ]}
        />
      </div>

      <Panel className={Layout.breakoutWideClassName}>
        <VersionsTable versions={replay} />
      </Panel>

      {prod ? (
        <Text tagName="div" size="small" color="subtle">
          For reference, real prod answers up to {shortDate(prod.date)} scored{' '}
          {percent(prod.quality)} with the live judge (different questions).
        </Text>
      ) : null}
    </Stack>
  );
};
